import { useEffect, useRef, useState, useMemo } from "react";

import { AgGridReact } from "ag-grid-react";
import { Autocomplete, Box, TextField } from "@mui/material"

import { cloneDeep, isEmpty, isNull, isUndefined, replace} from "lodash";
import { withTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { format } from "date-fns";

import { RPItemAutocomplete } from "@plannel/components/filter/ItemAutocomplete";
import rpMonthlyCategoryLocationService from "@plannel/services/rp/rp-monthly-category-location-service";
import SplitterContainer from "@plannel/components/SplitterContainer";
import DefaultGridSetting from "@plannel/components/aggrid/DefaultGridSetting"
import PivotChartComposed from "@plannel/components/rechart/PivotChartComposed";
import CustomHeaderMenuIcon from "@plannel/components/aggrid/CustomHeaderMenuIcon";
import MeasureModal from "@plannel/components/modal/MeasureModal";
import GridUtils from "@plannel/components/aggrid/GridUtils";
import Snackbar from "@plannel/components/Snackbar";
import debug from "@plannel/utils/debug-util";
import reduxUtil from "@plannel/utils/redux-util";
import NumberUtil from "@plannel/utils/number-util";
import { green } from '@mui/material/colors';
import CodeUtil from "@plannel/pages/utils/code-util";
import ColorUtil from "@plannel/pages/utils/color-util";
import FilterContainer from "@plannel/components/layout/FilterContainer";
import ItemHrchyTreeFilter from "@plannel/components/filter/ItemHrchyTreeFilter";
import PeriodFilter from "@plannel/components/filter/PeriodFilter";
import LocationTreeFilter from "@plannel/components/filter/LocationTreeFilter";
import RpVersionFilter from "@plannel/components/filter/RpVersionFilter";
import PaginationContainer from "@plannel/components/PaginationContainer";
import ExcelExportButton from "@plannel/components/ExcelExportButton";
import appConfigService from "@plannel/services/system/app-config-service";
import companyService from "@plannel/services/data/company-service";
import planConfigService from "@plannel/services/data/plan-config-service";

const CURRENT_DATE = new ZDate();
const INITIAL_START_DATE = new ZDate(CURRENT_DATE.getFullYear() - 1, CURRENT_DATE.getMonth() + 1, 1);
const INITIAL_END_DATE = new ZDate(CURRENT_DATE.getFullYear(), CURRENT_DATE.getMonth() + 3, 0);
const INITIAL_PERIOD = { start: INITIAL_START_DATE, end: INITIAL_END_DATE };
const SHIPMENT_QTY_TOTAL = "shipmentQtyTotal";
const SHIPMENT_AMT_TOTAL = "shipmentAmtTotal";

const MEASURE = CodeUtil.ipRpMeasure;
const { versionStatusCode, itemType, attributes } = CodeUtil;
const { snackBarMessageColor } = ColorUtil;
const { SIMULATION, PLANNED, RELEASED } = versionStatusCode;

const measureKeyMap = {
  [MEASURE.SHIPMENT_QTY]: "shipmentQtyRepl",
  [MEASURE.SHIPMENT_AMT]: "shipmentAmtRepl",
  [MEASURE.SALES_QTY]: "shipmentQtyFcst",
  [MEASURE.SALES_AMT]: "shipmentAmtFcst",
  [SHIPMENT_QTY_TOTAL]: "shipmentQty",
  [SHIPMENT_AMT_TOTAL]: "shipmentAmt"
};

const chartMeasureKeyMap = {
  [MEASURE.SHIPMENT_QTY]: MEASURE.SALES_QTY,
  [MEASURE.SHIPMENT_AMT]: MEASURE.SALES_AMT,
  [MEASURE.SALES_QTY]: MEASURE.SHIPMENT_QTY,
  [MEASURE.SALES_AMT]: MEASURE.SHIPMENT_AMT,
};


const RpMonthlyTrend = ({ t, viewName, title }) => {
  const reduxViewState = reduxUtil.getViewState(viewName);
  const reduxDispatch = useDispatch();

  const gridRef = useRef(null);
  const exportColumnDefs = useRef();
  const pageSizes = [10, 20, 50];
  const [currentPage, setCurrentPage] = useState(reduxViewState?.currentPage ? reduxViewState.currentPage : 1);
  const [tPages, setTotalPages] = useState(0);
  const [pageSize, setPageSize] = useState(reduxViewState?.pageSize ? reduxViewState.pageSize : pageSizes[0]);
  const [sortParams, setSortParams] = useState({});
  const resultTotalCount = useRef(null);
  const itemTypes = ["FG", "KIT"];
  const HRCHY_CD_GRP_COL_ID = "hrchyCdGrp";
  const HRCHY_NM_GRP_COL_ID = "hrchyNmGrp";
  const ITEM_CD_GRP_COL_ID = "itemCdGrp";
  const ITEM_NM_GRP_COL_ID = "itemNmGrp";
  const ITEM_ATTR_GRP_COL_ID = "itemAttrGrp";
  const [itemTypeSearch, setItemTypeSearch] = useState(reduxViewState?.itemTypeSearch ?? null);
  
  const [filterValue, setFilterValue] = useState(reduxViewState?.filter ?? { locations: null, category: null });
  const [rpVersionFilter, setRpVersionFilter] = useState(reduxViewState?.rpVersion ? reduxViewState.rpVersion : null);
  const [periodFilter, setPeriodFilter] = useState(
    reduxViewState?.period ?
      { start: new ZDate(reduxViewState.period.start), end: new ZDate(reduxViewState.period.end) }
      : INITIAL_PERIOD
  );

  const [latestInvDate, setLatestInvDate] = useState(null);

  const itemSearch = useRef(reduxViewState?.itemSearch ?? "");
  const extraItemCodesRef = useRef(reduxViewState?.extraItemCodes ?? []);
  const [gridData, setGridData] = useState([]);
  const [chartRaw, setChartRaw] = useState(null);
  const [gridLoading, setGridLoading] = useState(false);

  const [itemAttrSearch, setItemAttrSearch] = useState(reduxViewState?.itemAttrSearch ?? []);
  const [itemCustomConfig, setItemCustomConfig] = useState([]);
  const [isAttrGrouping, setIsAttrGrouping] = useState(false);

  const userData = JSON.parse(localStorage.getItem("user"));
  const symbol = userData.currencySymbol ?? "";
  const EXCEL_EXPORT = 'excelExport';

  const [displayName, setDisplayName] = useState(reduxViewState?.displayName ?? null);

  const getDisplayText = (translationKey, originTxt) => {
    if (displayName != null && displayName[translationKey]) {
      return displayName[translationKey];
    }
    return t(originTxt);
  };

  const defaultSeries = [
    { field: MEASURE.SKU, text: getDisplayText(MEASURE.SKU, MEASURE.SKU), type: "bar", orientation: "left", groupName: t("sku") },
    { field: MEASURE.ACTIVE_SKU, text: getDisplayText(MEASURE.ACTIVE_SKU, MEASURE.ACTIVE_SKU), type: "bar", orientation: "left", groupName: t("sku") },
    { field: MEASURE.AVG_NO_OF_LOCATION, text: getDisplayText(MEASURE.AVG_NO_OF_LOCATION, MEASURE.AVG_NO_OF_LOCATION), type: "line", orientation: "right" },
    { field: SHIPMENT_QTY_TOTAL, text: getDisplayText(MEASURE.SHIPMENT_QTY, MEASURE.SHIPMENT_QTY), type: "bar", orientation: "left", groupName: t("qty") },
    { field: SHIPMENT_AMT_TOTAL, text: getDisplayText(MEASURE.SHIPMENT_AMT, MEASURE.SHIPMENT_AMT), type: "line", orientation: "right", groupName: t("amt"), isAmt: true },
    { field: MEASURE.SALES_QTY, text: getDisplayText(MEASURE.SHIPMENT_QTY_REPL, MEASURE.SHIPMENT_QTY_REPL), type: "bar", orientation: "left", groupName: t("qty") },
    { field: MEASURE.SALES_AMT, text: getDisplayText(MEASURE.SHIPMENT_AMT_REPL, MEASURE.SHIPMENT_AMT_REPL), type: "line", orientation: "right", groupName: t("amt"), isAmt: true },
    { field: MEASURE.SHIPMENT_QTY, text: getDisplayText(MEASURE.SHIPMENT_QTY_FCST, MEASURE.SHIPMENT_QTY_FCST), type: "bar", orientation: "left", groupName: t("qty") },
    { field: MEASURE.SHIPMENT_AMT, text: getDisplayText(MEASURE.SHIPMENT_AMT_FCST, MEASURE.SHIPMENT_AMT_FCST), type: "line", orientation: "right", groupName: t("amt"), isAmt: true },
    { field: MEASURE.DEMAND_QTY, text: getDisplayText(MEASURE.DEMAND_QTY, MEASURE.DEMAND_QTY), type: "bar", orientation: "left", groupName: t("qty") },
    { field: MEASURE.DEMAND_AMT, text: getDisplayText(MEASURE.DEMAND_AMT, MEASURE.DEMAND_AMT), type: "line", orientation: "right", groupName: t("amt"), isAmt: true },
    { field: MEASURE.FCST_DEMAND_QTY, text: getDisplayText(MEASURE.FCST_DEMAND_QTY, MEASURE.FCST_DEMAND_QTY), type: "bar", orientation: "left", groupName: t("qty") },
    { field: MEASURE.FCST_DEMAND_AMT, text: getDisplayText(MEASURE.FCST_DEMAND_AMT, MEASURE.FCST_DEMAND_AMT), type: "line", orientation: "right", groupName: t("amt"), isAmt: true },
    { field: MEASURE.INTERNAL_DEMAND_QTY, text: getDisplayText(MEASURE.INTERNAL_DEMAND_QTY, MEASURE.INTERNAL_DEMAND_QTY), type: "bar", orientation: "left", groupName: t("qty") },
    { field: MEASURE.INTERNAL_DEMAND_AMT, text: getDisplayText(MEASURE.INTERNAL_DEMAND_AMT, MEASURE.INTERNAL_DEMAND_AMT), type: "line", orientation: "right", groupName: t("amt"), isAmt: true },
    { field: MEASURE.REPLENISH_PLAN_QTY, text: getDisplayText(MEASURE.REPLENISH_PLAN_QTY, MEASURE.REPLENISH_PLAN_QTY), type: "bar", orientation: "left", groupName: t("qty") },
    { field: MEASURE.REPLENISH_PLAN_AMT, text: getDisplayText(MEASURE.REPLENISH_PLAN_AMT, MEASURE.REPLENISH_PLAN_AMT), type: "line", orientation: "right", groupName: t("amt"), isAmt: true },
    { field: MEASURE.PURCHASE_PLAN_QTY, text: getDisplayText(MEASURE.PURCHASE_PLAN_QTY, MEASURE.PURCHASE_PLAN_QTY), type: "bar", orientation: "left", groupName: t("qty") },
    { field: MEASURE.PURCHASE_PLAN_AMT, text: getDisplayText(MEASURE.PURCHASE_PLAN_AMT, MEASURE.PURCHASE_PLAN_AMT), type: "line", orientation: "right", groupName: t("amt"), isAmt: true },
    { field: MEASURE.ARRIVAL_QTY, text: getDisplayText(MEASURE.ARRIVAL_QTY, MEASURE.ARRIVAL_QTY), type: "bar", orientation: "left", groupName: t("qty") },
    { field: MEASURE.ARRIVAL_AMT, text: getDisplayText(MEASURE.ARRIVAL_AMT, MEASURE.ARRIVAL_AMT), type: "line", orientation: "right", groupName: t("amt"), isAmt: true },
    { field: MEASURE.STOCK_QTY, text: getDisplayText(MEASURE.STOCK_QTY, MEASURE.STOCK_QTY), type: "bar", orientation: "left", groupName: t("qty") },
    { field: MEASURE.STOCK_AMT, text: getDisplayText(MEASURE.STOCK_AMT, MEASURE.STOCK_AMT), type: "line", orientation: "right", groupName: t("amt"), isAmt: true },
    { field: MEASURE.INV_DAYS, text: getDisplayText(MEASURE.INV_DAYS, MEASURE.INV_DAYS), type: "line", orientation: "left" },
    { field: MEASURE.INV_TURNOVER, text: getDisplayText(MEASURE.INV_TURNOVER, MEASURE.INV_TURNOVER), type: "line", orientation: "left" },
    { field: MEASURE.GROSS_PROFIT, text: getDisplayText(MEASURE.GROSS_PROFIT, MEASURE.GROSS_PROFIT), type: "line", orientation: "right", groupName: t("amt"), isAmt: true },
    { field: MEASURE.GROSS_MARGIN, text: getDisplayText(MEASURE.GROSS_MARGIN, MEASURE.GROSS_MARGIN), type: "line", orientation: "left" },
    { field: MEASURE.CROSSOVER_RATIO, text: getDisplayText(MEASURE.CROSSOVER_RATIO, MEASURE.CROSSOVER_RATIO), type: "line", orientation: "left" },
    { field: MEASURE.FILL_RATE_QTY, text: getDisplayText(MEASURE.FILL_RATE_QTY, MEASURE.FILL_RATE_QTY), type: "line", orientation: "left" },
    { field: MEASURE.LOSS_OF_OPPORTUNITY, text: getDisplayText(MEASURE.LOSS_OF_OPPORTUNITY, MEASURE.LOSS_OF_OPPORTUNITY), type: "line", orientation: "right", groupName: t("amt"), isAmt: true },
    { field: MEASURE.LAST_YEAR_INV_QTY, text: getDisplayText(MEASURE.LAST_YEAR_INV_QTY, MEASURE.LAST_YEAR_INV_QTY), type: "bar", orientation: "left", groupName: t("qty") },
    { field: MEASURE.LAST_YEAR_INV_AMT, text: getDisplayText(MEASURE.LAST_YEAR_INV_AMT, MEASURE.LAST_YEAR_INV_AMT), type: "line", orientation: "right", groupName: t("amt"), isAmt: true },
    { field: MEASURE.LAST_YEAR_SALES_QTY, text: getDisplayText(MEASURE.LAST_YEAR_SALES_QTY, MEASURE.LAST_YEAR_SALES_QTY), type: "bar", orientation: "left", groupName: t("qty") },
    { field: MEASURE.LAST_YEAR_SALES_AMT, text: getDisplayText(MEASURE.LAST_YEAR_SALES_AMT, MEASURE.LAST_YEAR_SALES_AMT), type: "line", orientation: "right", groupName: t("amt"), isAmt: true },
    { field: MEASURE.LAST_YEAR_GROSS_PROFIT, text: getDisplayText(MEASURE.LAST_YEAR_GROSS_PROFIT, MEASURE.LAST_YEAR_GROSS_PROFIT), type: "line", orientation: "right", groupName: t("amt"), isAmt: true },
  ];
  const [pivotConfig, setPivotConfig] = useState({
    groupKey: "measure"
  });

  const [measureModalOpen, setMeasureModalOpen] = useState(false);
  const [measures, setMeasures] = useState([]);

  /*
   * defaultSeries 는 렌더마다 t() 로 다시 만들어지는데, 여기서 걸러낸 결과를 state 에 담아 두면
   * 언어를 바꿔도 차트가 옛 언어로 남는다. 선택된 measure 목록에서 렌더 시점에 파생시킨다.
   */
  const chartSeries = useMemo(() => {
    if (isEmpty(measures)) {
      return [];
    }

    const knownFields = new Set(defaultSeries.map(series => series.field));
    const knownSeries = defaultSeries.filter(series => measures.includes(series.field));
    // 사용자 정의 measure — 이름이 곧 코드라 번역 대상이 아니다.
    const customSeries = measures
      .filter(measure => !knownFields.has(measure))
      .map(measure => ({ field: measure, text: measure, type: "bar", orientation: "left", groupName: t("qty") }));

    return [...knownSeries, ...customSeries];

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [measures, displayName, t]);

  /* 차트에 넘기는 설정 — 시리즈는 state 에 굳히지 않고 렌더 시점에 합친다. */
  const chartPivotConfig = useMemo(() => ({ ...pivotConfig, chartSeries }), [pivotConfig, chartSeries]);
  const allMeasure = useMemo(
    () => [MEASURE.SKU, MEASURE.ACTIVE_SKU, MEASURE.AVG_NO_OF_LOCATION, SHIPMENT_QTY_TOTAL, SHIPMENT_AMT_TOTAL, MEASURE.SALES_QTY, MEASURE.SALES_AMT, MEASURE.SHIPMENT_QTY, MEASURE.SHIPMENT_AMT, MEASURE.DEMAND_QTY, MEASURE.DEMAND_AMT, MEASURE.FCST_DEMAND_QTY, MEASURE.FCST_DEMAND_AMT, MEASURE.INTERNAL_DEMAND_QTY, MEASURE.INTERNAL_DEMAND_AMT, MEASURE.REPLENISH_PLAN_QTY, MEASURE.REPLENISH_PLAN_AMT, MEASURE.PURCHASE_PLAN_QTY, MEASURE.PURCHASE_PLAN_AMT, MEASURE.ARRIVAL_QTY, MEASURE.ARRIVAL_AMT, MEASURE.STOCK_QTY, MEASURE.STOCK_AMT, MEASURE.INV_DAYS, MEASURE.INV_TURNOVER, MEASURE.GROSS_PROFIT, MEASURE.GROSS_MARGIN, MEASURE.CROSSOVER_RATIO, MEASURE.FILL_RATE_QTY, MEASURE.LOSS_OF_OPPORTUNITY, MEASURE.LAST_YEAR_INV_QTY, MEASURE.LAST_YEAR_INV_AMT, MEASURE.LAST_YEAR_SALES_QTY, MEASURE.LAST_YEAR_SALES_AMT, MEASURE.LAST_YEAR_GROSS_PROFIT],
    []
  );

  const [snackInfo, setSnackInfo] = useState({ open: false, severity: "", content: "" });

  const defaultColumnDefs = 
  [{headerName: 'hrchyCd', colId: HRCHY_CD_GRP_COL_ID, width: 140, showRowGroup: 'hrchyCd', pinned: "left", lockPinned: true,
    type: ["nonEditableColumn"], cellRenderer: 'agGroupCellRenderer', hide: isAttrGrouping || itemSearch.current?.length > 0 || !filterValue.category?.length > 0,
    cellRendererParams: { suppressCount: true }, cellClass: 'stringType'
  },
  {
    headerName: 'hrchyName', colId: HRCHY_NM_GRP_COL_ID, width: 140, showRowGroup: 'hrchyNm', pinned: "left", lockPinned: true,
    type: ["nonEditableColumn"], cellRenderer: 'agGroupCellRenderer', hide: isAttrGrouping || itemSearch.current?.length > 0 || !filterValue.category?.length > 0,
    cellRendererParams: { suppressCount: true }
  },
  {
    headerName: 'itemCd', colId: ITEM_CD_GRP_COL_ID, width: 140, showRowGroup: 'itemCd', pinned: "left", lockPinned: true,
    type: ["nonEditableColumn"], cellRenderer: 'agGroupCellRenderer', hide: isAttrGrouping || !itemSearch.current?.length > 0,
    cellRendererParams: { suppressCount: true }, cellClass: 'stringType'
  },
  {
    headerName: 'itemName', colId: ITEM_NM_GRP_COL_ID, width: 140, showRowGroup: 'itemNm', pinned: "left", lockPinned: true,
    type: ["nonEditableColumn"], cellRenderer: 'agGroupCellRenderer', hide: isAttrGrouping || !itemSearch.current?.length > 0,
    cellRendererParams: { suppressCount: true }
  },
  {
    headerName: 'itemAttr', colId: ITEM_ATTR_GRP_COL_ID, width: 140, showRowGroup: 'itemAttr', pinned: "left", lockPinned: true,
    type: ["nonEditableColumn"], cellRenderer: 'agGroupCellRenderer', hide: !(isAttrGrouping),
    cellRendererParams: { suppressCount: true }, cellClass: 'stringType'
  },
  { headerName: "hrchyCd", field: "hrchyCd", rowGroup: true, hide: true, suppressColumnsToolPanel: true },
  { headerName: "hrchyNm", field: "hrchyNm", rowGroup: true, hide: true, suppressColumnsToolPanel: true },
  { headerName: "itemCd", field: "itemCd", rowGroup: true, hide: true, suppressColumnsToolPanel: true },
  { headerName: "itemNm", field: "itemNm", rowGroup: true, hide: true, suppressColumnsToolPanel: true },
  { headerName: "itemAttr", field: "itemAttr", rowGroup: true, hide: true, suppressColumnsToolPanel: true },
  {
    headerName: "measure", field: "measure", type: ["nonEditableColumn"], width: 150, pinned: "left", lockPinned: true,
    headerComponent: CustomHeaderMenuIcon,
    headerComponentParams: {
      enableMenu: false,
      isClicked: setMeasureModalOpen
    },
    valueFormatter: ({ data }) => getDisplayText(data?.originMeasure, data?.originMeasure)
  }];
  
  const [columnDefs, setColumnDefs] = useState(defaultColumnDefs);

  const defaultGridMemo = useMemo(() => {
    const defaultSettings = DefaultGridSetting({ title, viewName });
    const baseProcessCellForClipboard = defaultSettings.processCellForClipboard;

      // processCellForClipboard 오버라이드
    defaultSettings.processCellForClipboard = (params) => {
      const { column, node } = params;
      const fieldName = column.colDef?.field;
      const data = node.data;
    
      if (column.colDef?.isPivotColumn) {
        let rawValue = data?.[fieldName];
        
        if (isNull(rawValue) || isUndefined(rawValue) || isNaN(rawValue)) {
          return "0";
        }
        
        // formatterByMeasure 로직과 동일하게 처리

        const rationalColumns = [MEASURE.AVG_NO_OF_LOCATION, MEASURE.INV_DAYS, MEASURE.INV_TURNOVER, MEASURE.CROSSOVER_RATIO, MEASURE.GROSS_MARGIN, MEASURE.FILL_RATE_QTY];

        const computeColumns = Object.keys(data?.computeMeasureNameMap || {});

        const digits = rationalColumns.includes(data?.originMeasure) || computeColumns.includes(data?.originMeasure) ? 2 : 0;

        const amtColumns = [MEASURE.INV_AMT, MEASURE.DEMAND_AMT, MEASURE.STOCK_AMT, MEASURE.SALES_AMT, MEASURE.SHIPMENT_AMT, MEASURE.FCST_DEMAND_AMT, MEASURE.INTERNAL_DEMAND_AMT, MEASURE.REPLENISH_PLAN_AMT,
        MEASURE.PURCHASE_PLAN_AMT, MEASURE.ARRIVAL_AMT, MEASURE.GROSS_PROFIT, MEASURE.LOSS_OF_OPPORTUNITY, MEASURE.SHIPMENT_AMT_FCST, MEASURE.SHIPMENT_AMT_REPL, SHIPMENT_AMT_TOTAL,
        MEASURE.LAST_YEAR_INV_AMT, MEASURE.LAST_YEAR_SALES_AMT, MEASURE.LAST_YEAR_GROSS_PROFIT];

        if (amtColumns.includes(data?.originMeasure)) {
          return NumberUtil.formatNumber(rawValue, { 
            maximumFractionDigits: 2, 
            minimumFractionDigits: 2 
          }).replace(/,/g, "");
        } else {
          return NumberUtil.formatNumber(rawValue, { 
            maximumFractionDigits: digits, 
            minimumFractionDigits: digits 
          }).replace(/,/g, "");
        }
      }

      return baseProcessCellForClipboard(params);
    };

    return defaultSettings;

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getRequestParams = () => {
    const params = {};

    if (rpVersionFilter) {
      params.versionId = rpVersionFilter.id;
    }

    if (filterValue?.category) {
      params.hrchyCd = filterValue.category.hrchyCd;
    }

    if (filterValue?.category) {
      params.hrchyCdList = filterValue.category.filter((category) => category.leafFlg).map((category) => category.hrchyCd);
    }

    if (filterValue?.locations) {
      params.locationCd = filterValue?.locations?.map(location => location?.locCd);
    }

    if (periodFilter?.start) {
      params.startDate = periodFilter.start;
    }

    if (periodFilter?.end) {
      params.endDate = periodFilter.end;
    }

    if (itemTypeSearch) { 
      params.itemType = itemTypeSearch;
    }

    if (extraItemCodesRef.current?.length > 0) {
      params.items = [itemSearch.current, ...extraItemCodesRef.current].filter(Boolean);
    } 
    if (itemSearch.current) {
      params.item = itemSearch.current;
    }
    
    if (currentPage) {
      params.page = currentPage - 1;
    }

    if (pageSize) {
      params.pageSize = pageSize;
    }

    if (itemAttrSearch) {
      params.itemAttr = itemAttrSearch;
    }

    return { ...params, ...sortParams };
  };

  const formatterByMeasure = (params, isExport = false) => {
    const { data, colDef } = params;
    const fieldName = colDef?.field;
    let value = data?.[fieldName];

    if (isNull(value) || isUndefined(value) || isNaN(value)) {
      value = 0;
    }

    const rationalColumns = [MEASURE.AVG_NO_OF_LOCATION, MEASURE.INV_DAYS, MEASURE.INV_TURNOVER, MEASURE.CROSSOVER_RATIO, MEASURE.GROSS_MARGIN, MEASURE.FILL_RATE_QTY];

    const computeColumns = Object.keys(data?.computeMeasureNameMap || {});

    const digits = rationalColumns.includes(data?.originMeasure) || computeColumns.includes(data?.originMeasure) ? 2 : 0;

    const amtColumns = [MEASURE.INV_AMT, MEASURE.DEMAND_AMT, MEASURE.STOCK_AMT, MEASURE.SALES_AMT, MEASURE.SHIPMENT_AMT, MEASURE.FCST_DEMAND_AMT, MEASURE.INTERNAL_DEMAND_AMT, MEASURE.REPLENISH_PLAN_AMT,
      MEASURE.PURCHASE_PLAN_AMT, MEASURE.ARRIVAL_AMT, MEASURE.GROSS_PROFIT, MEASURE.LOSS_OF_OPPORTUNITY, MEASURE.SHIPMENT_AMT_FCST, MEASURE.SHIPMENT_AMT_REPL, SHIPMENT_AMT_TOTAL,
      MEASURE.LAST_YEAR_INV_AMT, MEASURE.LAST_YEAR_SALES_AMT, MEASURE.LAST_YEAR_GROSS_PROFIT];

    if (amtColumns.includes(data?.originMeasure)) {
      const formatValue = NumberUtil.formatNumber(value, { maximumFractionDigits: 2, minimumFractionDigits: 2 });
      if (isExport) {
        return formatValue.replace(/,/g, "");
      }
      if (value < 0) {
        return `-${symbol}${replace(formatValue, "-", "")}`;
      }
      return symbol + formatValue;
    } else {
      if (isExport) {
        return NumberUtil.formatNumber(value, { maximumFractionDigits: digits, minimumFractionDigits: digits }).replace(/,/g, "");
      }
      return NumberUtil.formatNumber(value, { maximumFractionDigits: digits, minimumFractionDigits: digits });
    }
  }
  formatterByMeasure.customType = EXCEL_EXPORT;

  const headerFormatter = (value) => {
    return format(new ZDate(value), "yyyy-MM");
  }

  const cellStyle = (params) => {
    const { colDef, data } = params;

    if (data?.latestInvDate && (new ZDate(colDef.field) > new ZDate(data.latestInvDate))) {
      return { background: green[50] };
    }
    return null;
  }

  const getExportDefaultColumnDefs = (itemAttrGroupingFlg) => {
    const hasItemSearch = itemSearch.current?.length > 0;
    const hasCategoryFilter = filterValue.category?.length > 0;

    let exportDefaultColumnDefs = [];

    const itemAttrFields = [
      { headerName: 'itemAttr', colId: ITEM_ATTR_GRP_COL_ID, rowGroup: true },
      { headerName: "itemAttr", field: "itemAttr", hide: !itemAttrGroupingFlg }
    ];

    const hrchyFields = [
      { field: "hrchyCd", colId: HRCHY_CD_GRP_COL_ID, rowGroup: true },
      { field: "hrchyNm", colId: HRCHY_NM_GRP_COL_ID, rowGroup: true },
      { headerName: "hrchyCd", field: "hrchyCd", hide: itemAttrGroupingFlg || itemSearch.current?.length > 0 || !filterValue.category?.length > 0, cellClass: 'stringType' },
      { headerName: "hrchyName", field: "hrchyNm", hide: itemAttrGroupingFlg || itemSearch.current?.length > 0 || !filterValue.category?.length > 0 }
    ];

    const itemFields = [
      { field: "itemCd", colId: ITEM_CD_GRP_COL_ID, rowGroup: true },
      { field: "itemName", colId: ITEM_NM_GRP_COL_ID, rowGroup: true},
      { headerName: "itemCd", field: "itemCd", hide: itemAttrGroupingFlg || !itemSearch.current?.length > 0, cellClass: 'stringType' },
      { headerName: "itemName", field: "itemNm", hide: itemAttrGroupingFlg || !itemSearch.current?.length > 0 }
    ];

    if (itemAttrGroupingFlg) {
      exportDefaultColumnDefs = [...itemAttrFields];
    } else if (hasItemSearch) {
      exportDefaultColumnDefs = [...itemFields];
    } else if(!hasItemSearch && hasCategoryFilter) {
      exportDefaultColumnDefs = [...hrchyFields];
    }

    // data.measure 에는 setGridDatas 에서 이미 표시명(displayName 우선, 없으면 번역)이 들어 있다.
    // export 공통 처리(processExportCell)는 valueFormatter 에 params.data 를 넘기지 않으므로
    // data 기반 valueFormatter 를 두면 엑셀에서 measure 가 빈칸이 된다.
    exportDefaultColumnDefs.push({ headerName: "measure", field: "measure", width: 150 });

    return exportDefaultColumnDefs;
  }

  const setGridHeaders = (headers, itemAttrGrouping) => {
    const dimensionDefs = cloneDeep(defaultColumnDefs);
    const exportColDefs = getExportDefaultColumnDefs(itemAttrGrouping);

    headers.forEach((header) => {
      dimensionDefs.push({
        headerName: headerFormatter(header), field: header, width: 120, sortable: false,
        colId: GridUtils.IGNORE_P13N_COLLID + '_' + header,
        type: ["numberColumn", "nonEditableColumn", "rightAligned"],
        cellStyle, valueFormatter: formatterByMeasure, isPivotColumn: true
      });

      exportColDefs.push({
        headerName: headerFormatter(header), field: header, width: 120, sortable: false,
        colId: GridUtils.IGNORE_P13N_COLLID + '_export_' + header, valueFormatter: formatterByMeasure, isPivotColumn: true
      });
    });

    exportColumnDefs.current = exportColDefs;
    gridRef.current?.api?.setGridOption('columnDefs', dimensionDefs);
    setColumnDefs(dimensionDefs);
  }

  const setGridDatas = (headers, results, isChart, computeMeasureNameMap) => {
    
    const datas = [];

    const skusPivotData = "skusPivotData";
    const activeSkusPivotData = "activeSkusPivotData";
    const avgNoOfLocationPivotData = "avgNoOfLocationPivotData";
    const demandQtyPivotData = "demandQtyPivotData";
    const demandAmtPivotData = "demandAmtPivotData";
    const fcstDemandQtyPivotData = "fcstDemandQtyPivotData";
    const fcstDemandAmtPivotData = "fcstDemandAmtPivotData";
    const internalDemandQtyPivotData = "internalDemandQtyPivotData";
    const internalDemandAmtPivotData = "internalDemandAmtPivotData";
    const shipmentQtyTotalPivotData = "shipmentQtyTotalPivotData";
    const shipmentAmtTotalPivotData = "shipmentAmtTotalPivotData";
    const salesQtyPivotData = "salesQtyPivotData";
    const salesAmtPivotData = "salesAmtPivotData";
    const shipmentQtyPivotData = "shipmentQtyPivotData";
    const shipmentAmtPivotData = "shipmentAmtPivotData";
    const stockQtyPivotData = "stockQtyPivotData";
    const stockAmtPivotData = "stockAmtPivotData";
    const invDaysPivotData = "invDaysPivotData";
    const invTurnoverPivotData = "invTurnoverPivotData";
    const grossProfitPivotData = "grossProfitPivotData";
    const grossMarginPivotData = "grossMarginPivotData";
    const crossoverRatioPivotData = "crossoverRatioPivotData";
    const replenishmentQtyPivotData = "replenishmentQtyPivotData";
    const replenishmentAmtPivotData = "replenishmentAmtPivotData";
    const purchaseQtyPivotData = "purchaseQtyPivotData";
    const purchaseAmtPivotData = "purchaseAmtPivotData";
    const arrivalQtyPivotData = "arrivalQtyPivotData";
    const arrivalAmtPivotData = "arrivalAmtPivotData";
    const fillRateQtyPivotData = "fillRateQtyPivotData";
    const lossOfOpportunityAmtPivotData = "lossOfOpportunityAmtPivotData";
    const lastYearInvQtyPivotData = "lastYearInvQtyPivotData";
    const lastYearInvAmtPivotData = "lastYearInvAmtPivotData";
    const lastYearSalesQtyPivotData = "lastYearSalesQtyPivotData";
    const lastYearSalesAmtPivotData = "lastYearSalesAmtPivotData";
    const lastYearGrossProfitPivotData = "lastYearGrossProfitPivotData";
    const computedMeasuresPivotData = "computedMeasuresPivotData";

    for (const resultsData of results) {
      const measure = isEmpty(measures) ? allMeasure : measures;
      for (const measureName of measure) {
        const translationKey = measureKeyMap[measureName] || measureName;
        const display = displayName?.[translationKey];
        const data = { ...resultsData, measure: display != null && !isEmpty(display) ? display : t(translationKey),
        originMeasure: translationKey};

        for (let idx = 0, len = headers.length; idx < len; idx++) {
          const header = headers[idx];

          switch (measureName) {
            case MEASURE.SKU:
              data[header] = resultsData[skusPivotData][idx];
              break;

            case MEASURE.ACTIVE_SKU:
              data[header] = resultsData[activeSkusPivotData][idx];
              break;

            case MEASURE.AVG_NO_OF_LOCATION:
              data[header] = resultsData[avgNoOfLocationPivotData][idx];
              break;

            case MEASURE.DEMAND_QTY:
              data[header] = resultsData[demandQtyPivotData][idx];
              break;
  
            case MEASURE.DEMAND_AMT:
              data[header] = resultsData[demandAmtPivotData][idx];
              break;

            case MEASURE.FCST_DEMAND_QTY:
              data[header] = resultsData[fcstDemandQtyPivotData][idx];
              break;

            case MEASURE.FCST_DEMAND_AMT:
              data[header] = resultsData[fcstDemandAmtPivotData][idx];
              break;

            case MEASURE.INTERNAL_DEMAND_QTY:
              data[header] = resultsData[internalDemandQtyPivotData][idx];
              break;
  
            case MEASURE.INTERNAL_DEMAND_AMT:
              data[header] = resultsData[internalDemandAmtPivotData][idx];
              break;

            case SHIPMENT_QTY_TOTAL:
              data[header] = resultsData[shipmentQtyTotalPivotData][idx];
              break;
    
            case SHIPMENT_AMT_TOTAL:
              data[header] = resultsData[shipmentAmtTotalPivotData][idx];
              break;

            case MEASURE.SALES_QTY:
              data[header] = resultsData[salesQtyPivotData][idx];
              break;
  
            case MEASURE.SALES_AMT:
              data[header] = resultsData[salesAmtPivotData][idx];
              break;

            case MEASURE.SHIPMENT_QTY:
              data[header] = resultsData[shipmentQtyPivotData][idx];
              break;
    
            case MEASURE.SHIPMENT_AMT:
              data[header] = resultsData[shipmentAmtPivotData][idx];
              break;
                
            case MEASURE.REPLENISH_PLAN_QTY:
              data[header] = resultsData[replenishmentQtyPivotData][idx];
              break;

            case MEASURE.REPLENISH_PLAN_AMT:
              data[header] = resultsData[replenishmentAmtPivotData][idx];
              break;
    
            case MEASURE.PURCHASE_PLAN_QTY:
              data[header] = resultsData[purchaseQtyPivotData][idx];
              break;

            case MEASURE.PURCHASE_PLAN_AMT:
              data[header] = resultsData[purchaseAmtPivotData][idx];
              break;

            case MEASURE.ARRIVAL_QTY:
              data[header] = resultsData[arrivalQtyPivotData][idx];
              break;

            case MEASURE.ARRIVAL_AMT:
              data[header] = resultsData[arrivalAmtPivotData][idx];
              break;

            case MEASURE.STOCK_QTY:
              data[header] = resultsData[stockQtyPivotData][idx];
              break;

            case MEASURE.STOCK_AMT:
              data[header] = resultsData[stockAmtPivotData][idx];
              break;

            case MEASURE.INV_DAYS:
              data[header] = resultsData[invDaysPivotData][idx];
              break;

            case MEASURE.INV_TURNOVER:
              data[header] = resultsData[invTurnoverPivotData][idx];
              break;

            case MEASURE.GROSS_PROFIT:
              data[header] = resultsData[grossProfitPivotData][idx];
              break;

            case MEASURE.GROSS_MARGIN:
              data[header] = resultsData[grossMarginPivotData][idx];
              break;

            case MEASURE.CROSSOVER_RATIO:
              data[header] = resultsData[crossoverRatioPivotData][idx];
              break;
            case MEASURE.FILL_RATE_QTY:
              data[header] = resultsData[fillRateQtyPivotData][idx];
              break;
            case MEASURE.LOSS_OF_OPPORTUNITY:
              data[header] = resultsData[lossOfOpportunityAmtPivotData][idx];
              break;
            case MEASURE.LAST_YEAR_INV_QTY:
              data[header] = resultsData[lastYearInvQtyPivotData][idx];
              break;
            case MEASURE.LAST_YEAR_INV_AMT:
              data[header] = resultsData[lastYearInvAmtPivotData][idx];
              break;
            case MEASURE.LAST_YEAR_SALES_QTY:
              data[header] = resultsData[lastYearSalesQtyPivotData][idx];
              break;
            case MEASURE.LAST_YEAR_SALES_AMT:
              data[header] = resultsData[lastYearSalesAmtPivotData][idx];
              break;
            case MEASURE.LAST_YEAR_GROSS_PROFIT:
              data[header] = resultsData[lastYearGrossProfitPivotData][idx];
              break;

            default:
              const lookupKey = computeMeasureNameMap[measureName] ? computeMeasureNameMap[measureName] : measureName.replace(/\s+/g, '_');
              data[header] = resultsData[computedMeasuresPivotData][lookupKey]?.[idx];
              break;
          }
        }

        if (isChart) {
          delete data.hrchyCd;
        }

        datas.push(data);
      }
    }

    return datas;
  }

  /* 차트 행은 원본에서 렌더 시점에 만든다 — measure 번역문이 chartSeries[].text 와 같은 언어여야 한다.
     의존성이 그대로면 배열 참조도 그대로라 PivotChartComposed 의 p13n 캐시는 그대로 산다. */
  const chartData = useMemo(() => {
    if (isEmpty(chartRaw)) {
      return [];
    }

    return setGridDatas(chartRaw.headers, chartRaw.data, true, chartRaw.computeMeasureNameMap);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chartRaw, measures, displayName, t]);

  const retrieveMonthlyTrendCategoryLocation = () => {
    const params = getRequestParams();

    gridRef.current?.api?.setGridOption('loading', true);
    rpMonthlyCategoryLocationService.getAll(params)
      .then(response => {
        const { pivotResult, totalPages, totalItems, message } = response.data;
        if (pivotResult) {
          const { localDateHeader, data } = pivotResult;

          if (pivotResult?.data && pivotResult.data.length > 0) {
            setIsAttrGrouping(data[0].itemAttrGrouping);
            setLatestInvDate(headerFormatter(data[0].latestInvDate));
            setGridHeaders(localDateHeader, data[0].itemAttrGrouping);
            setGridData(setGridDatas(localDateHeader, data, false, data[0].computeMeasureNameMap));
            if (!isEmpty(message)) {
              setSnackInfo({ open: true, severity: snackBarMessageColor.warning, content: t(message) });
            }
          } else {
            if (!isEmpty(message)) {
              setSnackInfo({ open: true, severity: snackBarMessageColor.warning, content: t("MSG_NoData") });  
            } else {
              setSnackInfo({ open: true, severity: snackBarMessageColor.dataEmpty, content: `${t("MSG_NoData")}\n${t(message)}` });
            }
            setGridHeaders([]);
            setGridData([]);
            setChartRaw(null);
            setPivotConfig({
              ...pivotConfig,
              include: []
            });
          }
        }
        setTotalPages(totalPages);
        resultTotalCount.current = totalItems;
      })
      .catch(e => {
        const { response } = e;

        if (response?.data?.errMsg) {
          setSnackInfo({ open: true, severity: snackBarMessageColor.warning, content: t(response.data.errMsg) });
        }

        debug(e);
      }) 
      .then(() => {
        const pageState = reduxUtil.createObjectState(viewName, { itemSearch: itemSearch.current, extraItemCodes: extraItemCodesRef.current,
          itemTypeSearch, itemAttrSearch, currentPage, pageSize, displayName });
        const filterState = reduxUtil.createFilterViewState(viewName, filterValue);
        const reduxState = { ...pageState, ...filterState };

        reduxDispatch(reduxUtil.updateViewState(reduxState));
      })
      .finally(() => {
        gridRef.current?.api?.setGridOption('loading', false);
      });
  };

  /* Montlhy PSI가 최신화 되어있는지 확인 */
  const monthlyPSICheck = async () => {
    const message = await appConfigService.asyncCheckMonthlyPSIData();
    if (message) {
      setSnackInfo({ open: true, severity: snackBarMessageColor.warning, content: t(message) });
    }
  }

  const retrieveMonthlyTrendCategoryLocationChart = () => {
    const params = getRequestParams();

    rpMonthlyCategoryLocationService.getChart({...params, pagination: false})
      .then(response => {
        const { pivotResult } = response.data;
        if (pivotResult) {
          const { localDateHeader, data } = pivotResult;

          if (pivotResult?.data && pivotResult.data.length > 0) {
            const headers = localDateHeader.map(header => headerFormatter(header));
            // chartSeries 는 chartPivotConfig 에서 렌더 시점에 합친다(번역문을 state 에 굳히지 않는다).
            setPivotConfig({
              ...pivotConfig,
              include: headers
            });
            setChartRaw({ headers, data, computeMeasureNameMap: data[0]?.computeMeasureNameMap ?? {} });
          } 
        }
      })
      .catch(e => {
        debug(e);
      })
  };

  const updateSeriesState = series => {
    const fieldsOnly = series.map(s => ({ field: s.field }));
    const reduxState = reduxUtil.createObjectState(viewName, { series: fieldsOnly });
    reduxDispatch(reduxUtil.updateViewState(reduxState));
  };

  const applyMeasures = selectedMeasures => {
    setMeasures(selectedMeasures);
  }

  const retrieveMonthlyTrendCategoryAll = () => {
    retrieveMonthlyTrendCategoryLocation();
    retrieveMonthlyTrendCategoryLocationChart();
  }

  const initPageToRetrieve = () => {
    resultTotalCount.current = null;
    if (currentPage > 1) {
      setCurrentPage(1);
      retrieveMonthlyTrendCategoryLocationChart();
    } else {
      retrieveMonthlyTrendCategoryAll();
    }
  }

  const getCustomConfig = () => {
    companyService.getCustomConfig()
      .then(response => {
        const data = response.data;
        const itemConfig = data.item;
        const customConfig = {};

        attributes.forEach(attr => {
          customConfig[attr] = itemConfig?.[attr] ? itemConfig[attr] : attr;
        });

        setItemCustomConfig(customConfig);
      })
      .catch(e => {
        debug(e);
      });
  }

  const retrieveP13n = () => {
    planConfigService.getMeasureDisplayNames(CodeUtil.module.RP)
      .then(response => {
        const data = response.data?.objVal;
        if (data && !isEmpty(data)) {
          const updatedDisplayName = { ...data };
          for (const [key, value] of Object.entries(measureKeyMap)) {
            // value가 measureKeyMap의 value에 존재하고
            // 기존 displayName에 해당 key가 없으면 추가
            if (!isEmpty(value) && !updatedDisplayName.hasOwnProperty(key)) {
              // i18n으로 번역된 텍스트를 사용하거나, 원본 key 사용
              if (updatedDisplayName[value] !== null) {
                updatedDisplayName[key] = updatedDisplayName[value];
              }
            }
          }
          setDisplayName(updatedDisplayName);
        }
      })
      .catch(e => {
        debug(e);
      })
      .finally(() => {
        setGridLoading(true);
      });
  }

  useEffect(() => {
    if (!isEmpty(columnDefs) && gridLoading) {
      const hideColumns = new Set();
      const visibleColumns = new Set();

      if (isAttrGrouping) {
        hideColumns.add(HRCHY_CD_GRP_COL_ID);
        hideColumns.add(HRCHY_NM_GRP_COL_ID);
        hideColumns.add(ITEM_CD_GRP_COL_ID);
        hideColumns.add(ITEM_NM_GRP_COL_ID);
        visibleColumns.add(ITEM_ATTR_GRP_COL_ID);
      } else {
        hideColumns.add(ITEM_ATTR_GRP_COL_ID);

        if(itemSearch.current?.length > 0 || !filterValue.category?.length > 0) {
          hideColumns.add(HRCHY_CD_GRP_COL_ID);
          hideColumns.add(HRCHY_NM_GRP_COL_ID);
        } else {
          visibleColumns.add(HRCHY_CD_GRP_COL_ID);
          visibleColumns.add(HRCHY_NM_GRP_COL_ID);
        }

        if(itemSearch.current?.length > 0) {
          visibleColumns.add(ITEM_CD_GRP_COL_ID);
          visibleColumns.add(ITEM_NM_GRP_COL_ID);
        } else {
          hideColumns.add(ITEM_CD_GRP_COL_ID);
          hideColumns.add(ITEM_NM_GRP_COL_ID);
        }
      }

      GridUtils.setColumnDefs({ ...gridRef.current, columnDefs, viewName});
      gridRef.current?.api?.setColumnsVisible([...visibleColumns], true);
      gridRef.current?.api?.setColumnsVisible([...hideColumns], false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columnDefs, filterValue, gridData, itemAttrSearch, isAttrGrouping])

  useEffect(() => {
    if (!isEmpty(measures)) {
      updateSeriesState(chartSeries);
      retrieveMonthlyTrendCategoryLocationChart();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [measures]);

  useEffect(() => {
    if (rpVersionFilter?.id) {
      retrieveMonthlyTrendCategoryLocationChart();
    }
    monthlyPSICheck();
    getCustomConfig();
    retrieveP13n()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (rpVersionFilter?.id && !isEmpty(measures)) {
      retrieveMonthlyTrendCategoryLocation();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, pageSize, sortParams, measures]);

  useEffect(() => {
    if (rpVersionFilter?.id && gridLoading) {
      initPageToRetrieve();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rpVersionFilter?.id, filterValue, periodFilter, itemTypeSearch, itemAttrSearch]);
  
  const onGridReady = params => {
    GridUtils.setColumnDefs({ ...params, columnDefs: defaultColumnDefs, viewName, initState: true });
  };

  const setExcelData = (headers, results, isChart = false) => {
    const computeMeasureNameMap = results?.[0]?.computeMeasureNameMap || {};

    return setGridDatas(headers, results, isChart, computeMeasureNameMap);
  };

  return (
    <>
      <FilterContainer>
        <RpVersionFilter viewName={viewName} rpVersionValue={rpVersionFilter} onChangeFilter={setRpVersionFilter} statusFilters={[SIMULATION, PLANNED, RELEASED]}/>
        <ItemHrchyTreeFilter viewName={viewName} onChangeFilter={setFilterValue} enableMultiSelect selectLeafs />
        <LocationTreeFilter viewName={viewName} filterValue={filterValue?.locations?.map(location => location?.locCd)} onChangeFilter={setFilterValue} />
        <PeriodFilter viewName={viewName} period={periodFilter} setPeriod={setPeriodFilter} dateType={"month"}/>
        <Autocomplete
            options={itemTypes}
            getOptionLabel={option => itemType ? t(itemType[option]?.name) : option}
            value={itemTypeSearch}
            onChange={(_, newValue) => setItemTypeSearch(newValue)}
            isOptionEqualToValue={(option, value) => option === value}
            size="small"
            sx={{ mr: 1, minWidth: 200 }}
            renderInput={(params) => (
              <TextField {...params} label={t("itemType")} />
            )}
        />
        <RPItemAutocomplete retrieve={initPageToRetrieve} searchValue={itemSearch} sx={{ mr: 1 }} key={"ITEM"} extraCodesRef={extraItemCodesRef} />
        <Autocomplete
            options={attributes || []}
            getOptionLabel={option => t(itemCustomConfig[option])}
            value={itemAttrSearch}
            onChange={(_, newValue) => setItemAttrSearch(newValue)}
            isOptionEqualToValue={(option, value) => option === value}
            size="small"
            sx={{ mr: 1, minWidth: 200 }}
            renderInput={(params) => (
              <TextField {...params} label={t("itemAttr")} />
            )}
        />
        <Box sx={{ ml: "auto" }}>
          <ExcelExportButton 
            title={title} 
            service={rpMonthlyCategoryLocationService}
            setExcelData={setExcelData}
            columnDefs={exportColumnDefs.current}
            params={{ ...getRequestParams() }} 
          />
        </Box>
      </FilterContainer>      <SplitterContainer initialSizes={[0.99, 1]} collapseDirection='up'>
        <Box className="chart-area" sx={{ height: "100%" }}>
          <PivotChartComposed
            key={`${measures.join(',')}-${chartSeries.length}`}
            pivotConfig={chartPivotConfig}
            data={chartData}
            allDataSeries={chartSeries}
            referenceLine={{x: {title: t("txnDate"), value : latestInvDate, y: null}}}
            options={{ decimalPoint: true, key: "chart" }}
            nullConvert
            activeMeasures={measures.map(measure => chartMeasureKeyMap[measure] || measure)}
          />
        </Box>
        <Box className="ag-theme-balham grid-area" sx={{ height: "calc(100% - 25px)", mt: 2, mb: 1 }}>
          <AgGridReact
            ref={gridRef}
            rowData={gridData}
            onSortChanged={params => setSortParams(GridUtils.getSortState(params))}
            {...defaultGridMemo}
            groupHideOpenParents
            groupDisplayType={'custom'}
            groupDefaultExpanded={11}
            onGridReady={onGridReady}
          />
        </Box>
      </SplitterContainer>
      <PaginationContainer
        currentPage={currentPage} totalPages={tPages}
        pageSize={pageSize} pageSizes={pageSizes}
        setCurrentPage={setCurrentPage} setPageSize={setPageSize}
      />
      <Snackbar
        open={snackInfo.open}
        onClose={() => setSnackInfo({ ...snackInfo, open: false })}
        severity={snackInfo.severity}
        content={snackInfo.content}
      />
      <MeasureModal
        open={measureModalOpen}
        setOpen={setMeasureModalOpen}
        viewName={viewName}
        allMeasure={allMeasure}
        applyMeasures={applyMeasures}
        measureKeyMap={measureKeyMap}
        enableCustomMeasure={true}
        displayNames={displayName}
        hasDisplayName={true}
      />
    </>
  );
};

export default withTranslation()(RpMonthlyTrend);